import { useNavigate, useParams } from 'react-router-dom';

import QuizStatusBadge from 'components/badge/QuizStatusBadge';
import IndicatorScreen from 'components/indicator/IndicatorScreen';
import { DivConst } from 'constants/divConst';

import WsModalReportDownload from 'pages/ws/components/modalReportDownload/WsModalReportDownload';
import WsRespondentDetailHeader from 'pages/ws/components/respondentDetailHeader/WsRespondentDetailHeader';
import WsPagination from 'pages/ws/components/pagination/WsPagination';

import { useWsRespondentQuiz } from './hooks/useWsRespondentQuiz';
import { formatTimeToDate, getDivLabel, isNotEmpty } from 'utils/app';
import { formatPublishTerm } from 'utils/quiz';

import type { JSX } from 'react';

/**
 * 獲得リスト詳細画面
 */
export default function WsRespondentQuiz(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const navigate = useNavigate();
  const { quizCd } = useParams();

  const {
    processing,
    quiz,
    pageNo,
    hasNext,
    pageStartNum,
    pageEndNum,
    modalOpen,
    setModalOpen,
    onModalReportDownloadOpen,
    onPrev,
    onNext,
  } = useWsRespondentQuiz(quizCd);

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {/* ローディング中の表示 */}
      <IndicatorScreen visible={processing} />

      {isNotEmpty(quiz) && (
        <>
          <WsRespondentDetailHeader
            title={quiz.title}
            createdAt={quiz.createdAt}
            createdAtLabel="クイズ作成日"
            publishTerm={formatPublishTerm({
              quizStatusDiv: quiz.quizStatusDiv,
              publishStartAt: quiz.publishStartAt,
              publishEndAt: quiz.publishEndAt,
              publishedAt: quiz.publishedAt,
              terminatedAt: quiz.terminatedAt,
            })}
            statusBadge={<QuizStatusBadge quizStatusDiv={quiz.quizStatusDiv} />}
            respondentCount={quiz.respondentCount}
            onBack={() => navigate(-1)}
            onModalReportDownloadOpen={onModalReportDownloadOpen}
          />

          <section className="section-user-detail">
            <div className="container">
              <div className="table-container table-responsive js-scrollable">
                <table className="table table-user">
                  <thead>
                    <tr>
                      <th className="col-2">
                        <p className="mb-0">メールアドレス</p>
                      </th>
                      <th className="col-2">回答日時</th>
                      <th className="col-2">正解数</th>
                      <th className="col-2">名前</th>
                      <th className="col-2">性別</th>
                      <th className="col-2">生年月日</th>
                      <th className="col-2">電話番号</th>
                      <th className="col-2">居住エリア</th>
                      <th className="col-2">自由項目1</th>
                      <th className="col-2">自由項目2</th>
                      <th className="col-2">自由項目3</th>
                    </tr>
                  </thead>

                  <tbody>
                    {quiz.respondents.map((respondent, index) => {
                      return (
                        <tr key={`ws-respondent-quiz-${index}`} className="respondent-table-row">
                          <td>
                            <div>{respondent.mailAddress}</div>
                          </td>
                          <td className="respondent-table-col">
                            <div>{formatTimeToDate(respondent.createdAt, 'yyyy/MM/dd HH:mm')}</div>
                          </td>
                          <td className="respondent-table-col">
                            <div>{respondent.correctCount}</div>
                          </td>
                          <td className="respondent-table-col">
                            <div>{respondent.respondentName}</div>
                          </td>
                          <td className="respondent-table-col">
                            <div>{getDivLabel('gender', respondent.genderDiv)}</div>
                          </td>
                          <td className="respondent-table-col">
                            <div>
                              {respondent.birthDate > 0 &&
                                formatTimeToDate(respondent.birthDate, 'yyyy/MM/dd')}
                            </div>
                          </td>
                          <td className="respondent-table-col">
                            <div>{respondent.phoneNumber}</div>
                          </td>
                          <td className="respondent-table-col">
                            <div>{getDivLabel('prefecture', respondent.prefectureDiv)}</div>
                          </td>
                          <td className="respondent-table-col">
                            <div className="respondent-table-cell">
                              {respondent.respondentCustomFieldAnswer01}
                            </div>
                          </td>
                          <td className="respondent-table-col">
                            <div className="respondent-table-cell">
                              {respondent.respondentCustomFieldAnswer02}
                            </div>
                          </td>
                          <td className="respondent-table-col">
                            <div className="respondent-table-cell">
                              {respondent.respondentCustomFieldAnswer03}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ページングボタン */}
              <WsPagination
                pageNo={pageNo}
                hasNext={hasNext}
                pageStartNum={pageStartNum}
                pageEndNum={pageEndNum}
                totalCount={quiz.respondentCount}
                onPrev={onPrev}
                onNext={onNext}
              />
            </div>
          </section>
        </>
      )}
      {/* レポートダウンロードモーダル */}
      <WsModalReportDownload
        open={modalOpen}
        setOpen={setModalOpen}
        cd={quizCd}
        contentsType={DivConst.items.contentsType.quiz.value}
        reportDateFrom={null}
        reportDateTo={null}
      />
    </>
  );
}
